import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { loyaltyApi } from '../services/loyaltyApi';
import { APP_BASE, appPath } from '../routes/appPaths';
import { useUserStore, selectIsSessionValid } from '../store/userStore';
import { applyTelegramChrome, getTelegramInitData, isTelegramMiniApp } from '../telegram/telegramWebApp';
import { TelegramAuthGate } from './TelegramAuthGate';

const SITE_PASSTHROUGH = [
  '/menu',
  '/tip/',
  '/hall-payment-return',
  '/payment-return',
  '/payment-failed',
  '/privacy-policy',
  '/delete-account',
  '/13ka',
  '/bio',
  '/cifra',
];

const EVENT_START_PARAM = /^e_([0-9a-f-]{36})$/i;

function shouldStayOnSite(pathname: string): boolean {
  if (pathname === APP_BASE || pathname.startsWith(`${APP_BASE}/`)) return true;
  return SITE_PASSTHROUGH.some((p) => pathname === p || pathname.startsWith(p));
}

function miniAppTarget(pathname: string): string {
  if (pathname.startsWith('/events')) return appPath(pathname);
  return appPath('/home');
}

function eventPathFromStartParam(): string | null {
  const raw = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
  if (!raw) return null;
  const m = EVENT_START_PARAM.exec(raw.trim());
  if (!m) return null;
  return appPath(`/events/${m[1]}`);
}

export function TelegramMiniApp() {
  const location = useLocation();
  const navigate = useNavigate();
  const login = useUserStore((s) => s.login);
  const isAuthed = useUserStore(selectIsSessionValid);
  const [hydrated, setHydrated] = useState(() => useUserStore.persist.hasHydrated());
  const [botUsername, setBotUsername] = useState<string | null>(null);
  const [needAuth, setNeedAuth] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [startParamHandled, setStartParamHandled] = useState(false);

  useEffect(() => {
    if (hydrated) return;
    return useUserStore.persist.onFinishHydration(() => setHydrated(true));
  }, [hydrated]);

  useEffect(() => {
    if (!isTelegramMiniApp()) return;
    applyTelegramChrome();
    const wa = window.Telegram?.WebApp;
    const onViewport = () => applyTelegramChrome();
    wa?.onEvent?.('viewportChanged', onViewport);
    wa?.onEvent?.('safeAreaChanged', onViewport);
    return () => {
      wa?.offEvent?.('viewportChanged', onViewport);
      wa?.offEvent?.('safeAreaChanged', onViewport);
    };
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    if (!isTelegramMiniApp()) {
      setSessionReady(true);
      setNeedAuth(false);
      return;
    }
    if (isAuthed) {
      setNeedAuth(false);
      setSessionReady(true);
      return;
    }
    const initData = getTelegramInitData();
    if (!initData) {
      setSessionReady(true);
      setNeedAuth(true);
      return;
    }
    let cancelled = false;
    setSessionReady(false);
    (async () => {
      try {
        const s = await loyaltyApi.telegramSession(initData);
        if (cancelled) return;
        setBotUsername(s.botUsername);
        if (s.linked) {
          login({
            phone: s.user.phone,
            userId: s.user.id,
            token: s.token,
            firstName: s.user.firstName,
            lastName: s.user.lastName,
          });
          setNeedAuth(false);
        } else {
          setNeedAuth(true);
        }
      } catch {
        if (!cancelled) setNeedAuth(true);
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrated, isAuthed, login]);

  useEffect(() => {
    if (!sessionReady || startParamHandled || !isTelegramMiniApp()) return;
    const target = eventPathFromStartParam();
    setStartParamHandled(true);
    if (!target) return;
    if (location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [sessionReady, startParamHandled, location.pathname, navigate]);

  if (isTelegramMiniApp() && !shouldStayOnSite(location.pathname)) {
    const fromStart = eventPathFromStartParam();
    return <Navigate to={fromStart ?? miniAppTarget(location.pathname)} replace />;
  }

  const inApp = location.pathname === APP_BASE || location.pathname.startsWith(`${APP_BASE}/`);
  const onLogin = location.pathname.startsWith(`${APP_BASE}/login`);
  if (inApp && !onLogin && sessionReady && needAuth && !isAuthed && isTelegramMiniApp()) {
    return <TelegramAuthGate botUsername={botUsername} />;
  }

  return null;
}
